/**
 * Editing the event mapping of a legacy MCE adapter.
 *
 * A legacy adapter's mapping is a small `Mce.Mapping` with an IDL file name, a
 * product and two event fields: `event_mapping_file`, a path on the VTS host, and
 * `event_mapping_data`, the XML itself. LiftCloud changes it only from a
 * `cdo_input` on `PUT /adapters/{id}`, which it accepts with `If-Match` naming the
 * bytes the page rendered (see `mceIfMatch`).
 *
 * `cdo_input` never rides an MCE body by default, because several screens PUT a
 * copy of the adapter they loaded (see `requestSerializers`). This is the one door:
 * the editor puts an edit under `MCE_EVENT_EDIT_KEY`, a key that no loaded adapter
 * has, and only the serializer turns it into `cdo_input`.
 */
import { AdapterCdoMceInterface } from "../../interfaces/Adapter.interface";
import { MceSaveFailure, READ_BACK_FAILED } from "./saveUpload";

/** The body key an event mapping edit travels under, from the editor to the serializer. */
export const MCE_EVENT_EDIT_KEY = "mce_event_mapping_edit";

const MAX_LOGGED_TEXT = 2000;

/** The two event fields of a legacy mapping. */
export interface MceEventFields {
  mapping_file: string;
  mapping_data: string;
}

/** A legacy mapping as `cdo_input` names it: the IDL file and product it keeps, and the event fields. */
export interface MceEventEdit {
  idl_file: string;
  product_type: number;
  event: MceEventFields;
}

const COULD_NOT_SAVE =
  "The event mapping could not be saved. Try again. If this continues, click Get log and send the file to technical support.";
const CHANGED_SINCE_OPENED =
  "This adapter changed since you opened it. Reload the page and edit the event mapping again.";
const TAKEN_OVER_BY_UPLOAD =
  "This adapter's mapping was replaced by an uploaded IDL since you opened it. Reload the page.";
const NO_PERMISSION = "You do not have permission to change this adapter's event mapping.";
const NOT_KEPT =
  "The server accepted the save but did not keep the event mapping. Click Get log and send the file to technical support.";

const STATUS_MESSAGES: Record<number, string> = {
  401: NO_PERMISSION,
  403: NO_PERMISSION,
  409: TAKEN_OVER_BY_UPLOAD,
  412: CHANGED_SINCE_OPENED,
  428: CHANGED_SINCE_OPENED,
};

/**
 * Whether this row's event mapping can be edited here. Only a legacy row that came
 * back with the event mapping it holds: that copy is what an edit is measured
 * against. An embedded row's uploaded XML wins in VTS, so an edit there would be
 * ignored without a word; re-uploading is its way to change it.
 */
export function isLegacyEventEditable(mce: AdapterCdoMceInterface): boolean {
  return mce.stored?.kind === "legacy" && mce.stored.savedEvent != null;
}

/**
 * The edit a save should carry, or null when the event mapping is what the server
 * holds. Strict equality: whitespace is part of an XML file, and clearing a field
 * is a change like any other.
 */
export function legacyEventEdit(mce: AdapterCdoMceInterface): MceEventEdit | null {
  const saved = mce.stored?.savedEvent;
  if (!isLegacyEventEditable(mce) || !saved) return null;
  if (
    mce.event.mapping_file === saved.mapping_file &&
    mce.event.mapping_data === saved.mapping_data
  ) {
    return null;
  }
  return {
    idl_file: mce.idl_file,
    product_type: mce.product_type,
    event: { mapping_file: mce.event.mapping_file, mapping_data: mce.event.mapping_data },
  };
}

/** The part of an adapter body an edit adds: nothing when there is no edit. */
export function eventEditBody(edit: MceEventEdit | null): Record<string, unknown> {
  return edit ? { [MCE_EVENT_EDIT_KEY]: edit } : {};
}

const UTF8_BOM = [0xef, 0xbb, 0xbf];
const UTF16_LE_BOM = [0xff, 0xfe];
const UTF16_BE_BOM = [0xfe, 0xff];

function startsWith(bytes: Uint8Array, prefix: number[]): boolean {
  return prefix.every((byte, index) => bytes[index] === byte);
}

/**
 * The text of an events file the browser read from disk. Real files carry a byte order
 * mark and some are UTF-16: a UTF-8 reader would turn those into garbage, and the
 * marks themselves would end up inside the XML that is saved. The mark is dropped.
 */
export function decodeEventsFile(bytes: Uint8Array): string {
  if (startsWith(bytes, UTF16_LE_BOM)) return new TextDecoder("utf-16le").decode(bytes.slice(2));
  if (startsWith(bytes, UTF16_BE_BOM)) return new TextDecoder("utf-16be").decode(bytes.slice(2));
  const body = startsWith(bytes, UTF8_BOM) ? bytes.slice(UTF8_BOM.length) : bytes;
  return new TextDecoder("utf-8").decode(body);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * The `cdo_input` JSON for an edit found under `MCE_EVENT_EDIT_KEY`, or null when
 * the value is not a whole edit. LiftCloud refuses a legacy `cdo_input` that names
 * no IDL file, so a partial edit is dropped here rather than sent to be refused.
 * It is JSON.stringify, not a hand-built string: the XML is multi-line and carries
 * quotes, tabs and non-ASCII text.
 */
export function cdoInputOfEventEdit(value: unknown): string | null {
  if (!isRecord(value) || !isRecord(value.event)) return null;
  const idlFile = textOf(value.idl_file);
  if (idlFile.trim() === "" || typeof value.product_type !== "number") return null;
  const event: MceEventFields = {
    mapping_file: textOf(value.event.mapping_file),
    mapping_data: textOf(value.event.mapping_data),
  };
  return JSON.stringify({ idl_file: idlFile, product_type: value.product_type, event });
}

function causeOf(error: unknown): { status: string; text: string } {
  if (isRecord(error)) {
    const status = typeof error.status === "number" ? `HTTP ${error.status}` : String(error.status);
    const body = error.data ?? error.error ?? "";
    return { status, text: typeof body === "string" ? body : JSON.stringify(body) };
  }
  return { status: "error", text: String(error) };
}

/** One plain message for a refused save, and what Get log records about it. */
export function eventEditFailure(error: unknown): MceSaveFailure {
  const status = isRecord(error) && typeof error.status === "number" ? error.status : 0;
  const { status: label, text } = causeOf(error);
  return {
    message: STATUS_MESSAGES[status] ?? COULD_NOT_SAVE,
    detail: `save: ${label}\n${text.slice(0, MAX_LOGGED_TEXT)}`,
  };
}

/**
 * The form's MCE state once an event mapping save has been answered. `stored` is what the
 * server holds now, read back, or null when it could not be read.
 *
 * The next precondition names the bytes in `stored`, so they replace the form's copy
 * whether or not the text was kept: a second save then carries an `If-Match` that
 * matches instead of one the server refuses as stale. The text in the fields stays as
 * the person left it, so anything the server did not keep is still a change to save.
 * Without the server's bytes the form keeps no copy to name, so its event mapping is
 * read-only until the page is reloaded.
 */
export function mceAfterEventSave(
  current: AdapterCdoMceInterface,
  stored: AdapterCdoMceInterface | null,
): AdapterCdoMceInterface {
  if (!stored?.stored) return { ...current, stored: { kind: "legacy", binaryBase64: null } };
  return { ...current, stored: stored.stored };
}

/**
 * Compare what the server holds after a save with what was sent. LiftCloud packs a
 * legacy `cdo_input` after it has answered the request's checks and, if the packing
 * fails, logs it and keeps the old bytes, so a 200 does not prove the text was kept.
 */
export function eventEditReadBack(
  sent: MceEventFields,
  stored: AdapterCdoMceInterface | null,
  adapterId: string,
): MceSaveFailure | null {
  if (!stored) {
    return {
      saved: true,
      message: READ_BACK_FAILED,
      detail: `read-back of ${adapterId} after a saved event mapping failed`,
    };
  }
  if (
    stored.event.mapping_file === sent.mapping_file &&
    stored.event.mapping_data === sent.mapping_data
  ) {
    return null;
  }
  return {
    message: NOT_KEPT,
    detail: [
      `read-back of ${adapterId} after a save differs from what was sent`,
      `mapping_file: sent ${sent.mapping_file.length} characters, stored ${stored.event.mapping_file.length}`,
      `mapping_data: sent ${sent.mapping_data.length} characters, stored ${stored.event.mapping_data.length}`,
    ].join("\n"),
  };
}
